import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { apiGet, apiPost, apiPut, apiDelete, apiPostFormData } from '../services/api';

type EmploymentStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
type ContractType = 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | null;

export interface Branch {
  id: number;
  name: string;
  code: string;
  address: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface Role {
  id: number;
  name: string;
  guard_name: string;
  created_at: string;
  updated_at: string;
}

export interface Employee {
  id: number;
  branch_id: number;
  name: string;
  email: string;
  employment_status: EmploymentStatus;
  contract_type: ContractType;
  contract_photo_path: string | null;
  profile_photo_path: string | null;
  salary: number | null;
  password: string | null;
  available_leave: number;
  available_sick_leave: number;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
  branch: Branch;
  role_name: string;
  isFullTime: boolean;
  isOnline: boolean;
}

export async function getMany({
  paginationModel,
  filterModel,
  sortModel,
  branchId,
}: {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  branchId?: number;
}): Promise<{ items: Employee[]; itemCount: number }> {
  const params = new URLSearchParams();
  params.append('per_page', String(paginationModel.pageSize));
  
  if (paginationModel.pageSize >= 100) {
    params.append('page', '1');
  } else {
    params.append('page', String(paginationModel.page + 1)); // API uses 1-based page numbers
  }
  
  const endpoint = `/api/employees?${params.toString()}`;
  const response = await apiGet<{ data: Employee[] } | Employee[]>(endpoint);
  const employees = Array.isArray(response) ? response : response.data;
  let filteredEmployees = [...employees];

  // Filter by branch if branchId is provided
  if (branchId !== undefined) {
    filteredEmployees = filteredEmployees.filter((employee) => employee.branch_id === branchId);
  }
  // Apply filters (example only)
  if (filterModel?.items?.length) {
    filterModel.items.forEach(({ field, value, operator }) => {
      if (!field || value == null) {
        return;
      }

      filteredEmployees = filteredEmployees.filter((employee) => {
        const employeeValue = employee[field as keyof Employee];

        switch (operator) {
          case 'contains':
            return String(employeeValue).toLowerCase().includes(String(value).toLowerCase());
          case 'equals':
            return employeeValue === value;
          case 'startsWith':
            return String(employeeValue).toLowerCase().startsWith(String(value).toLowerCase());
          case 'endsWith':
            return String(employeeValue).toLowerCase().endsWith(String(value).toLowerCase());
          case '>':
            return employeeValue > value;
          case '<':
            return employeeValue < value;
          default:
            return true;
        }
      });
    });
  }

  // Apply sorting
  if (sortModel?.length) {
    filteredEmployees.sort((a, b) => {
      for (const { field, sort } of sortModel) {
        if (a[field as keyof Employee] < b[field as keyof Employee]) {
          return sort === 'asc' ? -1 : 1;
        }
        if (a[field as keyof Employee] > b[field as keyof Employee]) {
          return sort === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
  }

  if (paginationModel.pageSize >= 100) {
    return {
      items: filteredEmployees,
      itemCount: filteredEmployees.length,
    };
  } else {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    const paginatedEmployees = filteredEmployees.slice(start, end);

    return {
      items: paginatedEmployees,
      itemCount: filteredEmployees.length,
    };
  }
}

export async function getAllEmployees(): Promise<Employee[]> {
  const params = new URLSearchParams();
  params.append('per_page', '1000');
  params.append('page', '1');
  
  const endpoint = `/api/employees?${params.toString()}`;
  const response = await apiGet<{ data: Employee[] } | Employee[]>(endpoint);
  return Array.isArray(response) ? response : response.data;
}

export async function getOne(employeeId: number) {
  return apiGet<Employee>(`/api/employees/${employeeId}`);
}

export async function createOne(data: Partial<Omit<Employee, 'id' | 'created_at' | 'updated_at' | 'email_verified_at' | 'branch'>>) {
  return apiPost<Employee>('/api/employees', data);
}

export async function updateOne(employeeId: number, data: Partial<Omit<Employee, 'id' | 'created_at' | 'updated_at' | 'email_verified_at' | 'branch'>>) {
  return apiPut<Employee>(`/api/employees/${employeeId}`, data);
}

export async function deleteOne(employeeId: number) {
  return apiDelete<void>(`/api/employees/${employeeId}`);
}

export interface StatisticsCounts {
  CREATED: number;
  COMPLETED: number;
  BOOKED: number;
  CANCELED: number;
}

export interface EmployeeStatistics {
  counts: StatisticsCounts;
  total: number;
}

export async function getEmployeeStatistics(
  employeeId: number,
  dateFrom?: string | null,
  dateTo?: string | null
): Promise<EmployeeStatistics> {
  const params = new URLSearchParams();
  params.append('employee_id', String(employeeId));

  if (dateFrom) {
    params.append('since', dateFrom);
  } else {
    params.append('since', '2025-01-01');
  }

  if (dateTo) {
    params.append('until', dateTo);
  }

  return apiGet<EmployeeStatistics>(`/api/orders-statistics?${params.toString()}`);
}

export async function uploadContractImage(
  employeeId: number,
  image: File,
): Promise<{ media: Array<{ url: string }> }> {
  const formData = new FormData();
  formData.append('resource_type', 'employee_contract');
  formData.append('resource_id', employeeId.toString());
  formData.append('collection', 'contract');
  formData.append('files[]', image);

  const response = await apiPostFormData<{ media: Array<{ url: string }> }>(
    `/api/upload`,
    formData,
  );

  return response;
}

export async function uploadProfileImage(
  employeeId: number,
  image: File,
): Promise<{ media: Array<{ url: string }> }> {
  const formData = new FormData();
  formData.append('resource_type', 'employee_profile');
  formData.append('resource_id', employeeId.toString());
  formData.append('collection', 'profile');
  formData.append('files[]', image);

  const response = await apiPostFormData<{ media: Array<{ url: string }> }>(
    `/api/upload`,
    formData,
  );

  return response;
}

// Validation follows the [Standard Schema](https://standardschema.dev/).

type ValidationResult = { issues: { message: string; path: (keyof Employee)[] }[] };

export function validate(employee: Partial<Employee>, isCreateMode: boolean = false): ValidationResult {
  let issues: ValidationResult['issues'] = [];

  if (!employee.name) {
    issues = [...issues, { message: 'validation.nameRequired', path: ['name'] }];
  }

  if (!employee.email) {
    issues = [...issues, { message: 'validation.emailRequired', path: ['email'] }];
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employee.email)) {
    issues = [...issues, { message: 'validation.invalidEmailFormat', path: ['email'] }];
  }

  // Password is required only in create mode
  if (isCreateMode && !employee.password) {
    issues = [...issues, { message: 'validation.passwordRequired', path: ['password'] }];
  }

  // If password is provided, validate its length
  if (employee.password && employee.password.length < 8) {
    issues = [...issues, { message: 'validation.passwordLengthRequired', path: ['password'] }];
  }

  if (!employee.branch_id) {
    issues = [...issues, { message: 'validation.branchRequired', path: ['branch_id'] }];
  }

  if (!employee.employment_status) {
    issues = [...issues, { message: 'validation.required', path: ['employment_status'] }];
  } else if (!['ACTIVE', 'INACTIVE', 'SUSPENDED'].includes(employee.employment_status)) {
    issues = [
      ...issues,
      { message: 'validation.required', path: ['employment_status'] },
    ];
  }

  return { issues };
}
